import { useRef, useCallback } from 'react';
import { FunctionEntry, PhysicsObject, GraphSettings, CustomGridLine, CustomGridSet } from '@/types/simulation';
import { computeGraphPoints } from '@/utils/mathParser';
import { worldToCanvas, objectRadiusWorld } from '@/utils/physics';
import { ThemeMode } from '@/contexts/ThemeContext';

interface DrawOptions {
  functions: FunctionEntry[];
  objects: PhysicsObject[];
  settings: GraphSettings;
  showTrails: boolean;
  selectedObjectId: string | null;
  theme?: ThemeMode;
}

// Theme-based canvas palette
function getCanvasPalette(theme: ThemeMode) {
  if (theme === 'dark') {
    return {
      bgStart:    '#0f0f1e',
      bgEnd:      '#1a1a2e',
      gridMinor:  'rgba(255,255,255,0.05)',
      gridMajor:  'rgba(255,255,255,0.12)',
      axis:       'rgba(255,255,255,0.50)',
      axisLabel:  'rgba(255,255,255,0.50)',
    };
  }
  if (theme === 'light') {
    return {
      bgStart:    '#f5f7ff',
      bgEnd:      '#edf0fb',
      gridMinor:  'rgba(0,0,0,0.04)',
      gridMajor:  'rgba(0,0,0,0.10)',
      axis:       'rgba(0,0,0,0.50)',
      axisLabel:  'rgba(0,0,0,0.55)',
    };
  }
  // picture
  return {
    bgStart:    '#ffffff',
    bgEnd:      '#f8f8ff',
    gridMinor:  'rgba(0,0,0,0.05)',
    gridMajor:  'rgba(0,0,0,0.14)',
    axis:       'rgba(0,0,0,0.70)',
    axisLabel:  'rgba(0,0,0,0.70)',
  };
}

// ---- Adaptive grid step: pick a "nice" step so labels don't crowd ----
function calcAdaptiveStep(rangeMin: number, rangeMax: number, canvasPixels: number, minGapPx = 60): number {
  const range = rangeMax - rangeMin;
  const rawStep = (range / canvasPixels) * minGapPx;
  // Round to nearest 1, 2, 5, 10, 20, 50...
  const mag = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const norm = rawStep / mag;
  let nice: number;
  if (norm < 1.5) nice = 1;
  else if (norm < 3.5) nice = 2;
  else if (norm < 7.5) nice = 5;
  else nice = 10;
  return nice * mag;
}

// ---- Format tick label smartly (avoid floating point noise) ----
function fmtTick(v: number, step: number): string {
  if (Math.abs(v) < step * 0.001) return '0';
  // Figure out needed decimal places from step
  const decimals = Math.max(0, -Math.floor(Math.log10(step)));
  return decimals > 0 ? v.toFixed(decimals) : String(Math.round(v));
}

export function useGraphCanvas(_width: number, _height: number) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const draw = useCallback(
    ({ functions, objects, settings, showTrails, selectedObjectId, theme = 'dark' }: DrawOptions) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const width = canvas.width;
      const height = canvas.height;
      const { xMin, xMax, yMin, yMax, gridVisible, customLines, customSets } = settings;
      const coordSys = { settings, width, height };
      const pal = getCanvasPalette(theme);

      // ---- Zoom-aware metrics ----
      const xRange = xMax - xMin;
      const yRange = yMax - yMin;
      // pixels per world unit
      const pxPerUnitX = width / xRange;
      const pxPerUnitY = height / yRange;
      const pxPerUnit = Math.min(pxPerUnitX, pxPerUnitY);

      // Reference zoom: at xRange=20 (default -10..10), pxPerUnit ≈ width/20
      // We use a log scale so the line width feels natural across zoom levels
      const refPxPerUnit = width / 20;
      const zoomRatio = pxPerUnit / refPxPerUnit;           // >1 = zoomed in, <1 = zoomed out
      const zoomLog = Math.log2(Math.max(zoomRatio, 0.01)); // negative = zoomed out

      // Adaptive grid step — overrides manual gridStep so labels are always readable
      const adaptiveStepX = calcAdaptiveStep(xMin, xMax, width, 70);
      const adaptiveStepY = calcAdaptiveStep(yMin, yMax, height, 55);
      const gridStep = Math.max(adaptiveStepX, adaptiveStepY);
      const minorStep = gridStep / 5;

      // Dynamic line widths
      const fnLineWidth  = Math.max(1.0, Math.min(4.0, 2.2 + zoomLog * 0.35));  // function curve
      const axisWidth    = Math.max(1.0, Math.min(3.0, 1.8 + zoomLog * 0.25));  // X/Y axes
      const majorWidth   = Math.max(0.4, Math.min(1.5, 0.8 + zoomLog * 0.12));  // major grid
      const minorWidth   = Math.max(0.2, Math.min(0.8, 0.4 + zoomLog * 0.06));  // minor grid

      // Dynamic font size for tick labels
      const fontSize = Math.max(9, Math.min(14, 11 + zoomLog * 1.2));

      // ---- Background ----
      ctx.clearRect(0, 0, width, height);
      const bg = ctx.createLinearGradient(0, 0, 0, height);
      bg.addColorStop(0, pal.bgStart);
      bg.addColorStop(1, pal.bgEnd);
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, width, height);

      // ---- Grid ----
      if (gridVisible) {
        ctx.save();
        // Minor grid (only show if not too crowded)
        const minorPxGap = minorStep * pxPerUnitX;
        if (minorPxGap >= 10) {
          ctx.strokeStyle = pal.gridMinor;
          ctx.lineWidth = minorWidth;
          for (let gx = Math.ceil(xMin / minorStep) * minorStep; gx <= xMax + 1e-9; gx += minorStep) {
            const { cx } = worldToCanvas(gx, 0, coordSys);
            ctx.beginPath(); ctx.moveTo(cx, 0); ctx.lineTo(cx, height); ctx.stroke();
          }
          for (let gy = Math.ceil(yMin / minorStep) * minorStep; gy <= yMax + 1e-9; gy += minorStep) {
            const { cy } = worldToCanvas(0, gy, coordSys);
            ctx.beginPath(); ctx.moveTo(0, cy); ctx.lineTo(width, cy); ctx.stroke();
          }
        }
        // Major grid
        ctx.strokeStyle = pal.gridMajor;
        ctx.lineWidth = majorWidth;
        for (let gx = Math.ceil(xMin / gridStep) * gridStep; gx <= xMax + 1e-9; gx += gridStep) {
          const { cx } = worldToCanvas(gx, 0, coordSys);
          ctx.beginPath(); ctx.moveTo(cx, 0); ctx.lineTo(cx, height); ctx.stroke();
        }
        for (let gy = Math.ceil(yMin / gridStep) * gridStep; gy <= yMax + 1e-9; gy += gridStep) {
          const { cy } = worldToCanvas(0, gy, coordSys);
          ctx.beginPath(); ctx.moveTo(0, cy); ctx.lineTo(width, cy); ctx.stroke();
        }
        ctx.restore();
      }

      // ---- Custom grid lines (draw BEFORE axes so labels appear on top) ----
      if (customLines) {
        customLines.forEach((line) => {
          if (line.axis === 'x') {
            const { cx } = worldToCanvas(line.position, 0, coordSys);
            if (cx < 0 || cx > width) return;
            ctx.save();
            ctx.strokeStyle = line.color;
            ctx.lineWidth = line.width;
            ctx.beginPath(); ctx.moveTo(cx, 0); ctx.lineTo(cx, height); ctx.stroke();
            if (line.showLabel) {
              ctx.fillStyle = line.color;
              ctx.font = `bold ${Math.max(10, Math.min(14, line.width * 3 + 6))}px Inter, sans-serif`;
              ctx.textAlign = 'center';
              ctx.textBaseline = 'bottom';
              const label = line.label ?? String(line.position);
              ctx.fillText(label, cx, height - 4);
            }
            ctx.restore();
          } else {
            const { cy } = worldToCanvas(0, line.position, coordSys);
            if (cy < 0 || cy > height) return;
            ctx.save();
            ctx.strokeStyle = line.color;
            ctx.lineWidth = line.width;
            ctx.beginPath(); ctx.moveTo(0, cy); ctx.lineTo(width, cy); ctx.stroke();
            if (line.showLabel) {
              ctx.fillStyle = line.color;
              ctx.font = `bold ${Math.max(10, Math.min(14, line.width * 3 + 6))}px Inter, sans-serif`;
              ctx.textAlign = 'left';
              ctx.textBaseline = 'middle';
              const label = line.label ?? String(line.position);
              ctx.fillText(label, 6, cy);
            }
            ctx.restore();
          }
        });
      }

      // ---- Custom grid sets (regular intervals) ----
      if (customSets) {
        customSets.forEach((set) => {
          if (!set.enabled) return;
          const positions: number[] = [];
          if (set.step > 0) {
            for (let p = set.start; p <= set.end + 1e-9; p += set.step) {
              positions.push(p);
            }
          }
          positions.forEach((pos) => {
            if (set.axis === 'x') {
              const { cx } = worldToCanvas(pos, 0, coordSys);
              if (cx < 0 || cx > width) return;
              ctx.save();
              ctx.strokeStyle = set.color;
              ctx.lineWidth = set.width;
              ctx.beginPath(); ctx.moveTo(cx, 0); ctx.lineTo(cx, height); ctx.stroke();
              if (set.showLabels) {
                ctx.fillStyle = set.color;
                ctx.font = `bold ${Math.max(9, Math.min(13, set.width * 2.5 + 5))}px Inter, sans-serif`;
                ctx.textAlign = 'center';
                ctx.textBaseline = 'bottom';
                ctx.fillText(String(Math.round(pos * 1000) / 1000), cx, height - 4);
              }
              ctx.restore();
            } else {
              const { cy } = worldToCanvas(0, pos, coordSys);
              if (cy < 0 || cy > height) return;
              ctx.save();
              ctx.strokeStyle = set.color;
              ctx.lineWidth = set.width;
              ctx.beginPath(); ctx.moveTo(0, cy); ctx.lineTo(width, cy); ctx.stroke();
              if (set.showLabels) {
                ctx.fillStyle = set.color;
                ctx.font = `bold ${Math.max(9, Math.min(13, set.width * 2.5 + 5))}px Inter, sans-serif`;
                ctx.textAlign = 'left';
                ctx.textBaseline = 'middle';
                ctx.fillText(String(Math.round(pos * 1000) / 1000), 6, cy);
              }
              ctx.restore();
            }
          });

          // Intersection dots and labels
          if (set.showDots || set.showLabels) {
            const otherSets = customSets.filter((s) => s.enabled && s.id !== set.id && s.axis !== set.axis);
            positions.forEach((pos) => {
              otherSets.forEach((other) => {
                const otherPositions: number[] = [];
                if (other.step > 0) {
                  for (let p = other.start; p <= other.end + 1e-9; p += other.step) {
                    otherPositions.push(p);
                  }
                }
                otherPositions.forEach((otherPos) => {
                  const ix = set.axis === 'x' ? pos : otherPos;
                  const iy = set.axis === 'x' ? otherPos : pos;
                  const { cx, cy } = worldToCanvas(ix, iy, coordSys);
                  if (cx < 0 || cx > width || cy < 0 || cy > height) return;
                  ctx.save();
                  if (set.showDots) {
                    ctx.fillStyle = set.color;
                    ctx.beginPath();
                    ctx.arc(cx, cy, Math.max(2, Math.min(5, set.width)), 0, Math.PI * 2);
                    ctx.fill();
                  }
                  if (set.showLabels) {
                    ctx.fillStyle = set.color;
                    ctx.font = `bold 10px Inter, sans-serif`;
                    ctx.textAlign = 'left';
                    ctx.textBaseline = 'bottom';
                    ctx.fillText(`(${Math.round(ix * 100) / 100},${Math.round(iy * 100) / 100})`, cx + 5, cy - 3);
                  }
                  ctx.restore();
                });
              });
            });
          }
        });
      }

      // ---- Custom intersection points ----
      const customIntersections = settings.customIntersections ?? [];
      const showCustomIntersections = settings.showCustomIntersections !== false;
      if (!showCustomIntersections) { /* skip all */ } else
      customIntersections.forEach((pt) => {
        if (pt.visible === false) return;
        const { cx, cy } = worldToCanvas(pt.x, pt.y, coordSys);
        if (cx < 0 || cx > width || cy < 0 || cy > height) return;
        ctx.save();
        // Outer ring
        ctx.strokeStyle = pt.color;
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.arc(cx, cy, Math.max(3, Math.min(10, pt.size)) + 3, 0, Math.PI * 2);
        ctx.stroke();
        // Fill dot
        ctx.fillStyle = pt.color;
        ctx.beginPath();
        ctx.arc(cx, cy, Math.max(2, Math.min(8, pt.size)), 0, Math.PI * 2);
        ctx.fill();
        if (pt.showLabel) {
          ctx.fillStyle = pt.color;
          ctx.font = `bold ${Math.max(9, Math.min(13, pt.size * 1.5 + 6))}px Inter, sans-serif`;
          ctx.textAlign = 'left';
          ctx.textBaseline = 'bottom';
          const label = pt.label ?? `(${Math.round(pt.x * 100) / 100}, ${Math.round(pt.y * 100) / 100})`;
          ctx.fillText(label, cx + pt.size + 5, cy - pt.size - 2);
        }
        ctx.restore();
      });

      // ---- Axes ----
      ctx.save();
      const origin = worldToCanvas(0, 0, coordSys);
      ctx.strokeStyle = pal.axis;
      ctx.lineWidth = axisWidth;
      ctx.beginPath(); ctx.moveTo(0, origin.cy); ctx.lineTo(width, origin.cy); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(origin.cx, 0); ctx.lineTo(origin.cx, height); ctx.stroke();

      // Tick labels
      ctx.fillStyle = pal.axisLabel;
      ctx.font = `${fontSize}px Inter, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      for (let gx = Math.ceil(xMin / gridStep) * gridStep; gx <= xMax + 1e-9; gx += gridStep) {
        if (Math.abs(gx) < gridStep * 0.001) continue;
        const { cx } = worldToCanvas(gx, 0, coordSys);
        const labelY = Math.min(Math.max(origin.cy + 4, 4), height - fontSize - 2);
        ctx.fillText(fmtTick(gx, gridStep), cx, labelY);
      }
      ctx.textAlign = 'right';
      ctx.textBaseline = 'middle';
      for (let gy = Math.ceil(yMin / gridStep) * gridStep; gy <= yMax + 1e-9; gy += gridStep) {
        if (Math.abs(gy) < gridStep * 0.001) continue;
        const { cy } = worldToCanvas(0, gy, coordSys);
        const labelX = Math.min(Math.max(origin.cx - 6, 6), width - 6);
        ctx.fillText(fmtTick(gy, gridStep), labelX, cy);
      }
      ctx.restore();

      // ---- Functions ----
      // 物体が多いほどサンプル点を減らして描画負荷を下げる
      const baseSamples = objects.length >= 3 ? 400 : objects.length >= 2 ? 500 : 700;
      functions
        .filter((f) => f.visible && !f.error && f.expression.trim())
        .forEach((fn) => {
          const sampleCount = Math.round(Math.min(baseSamples, Math.max(300, width * (0.5 + zoomRatio * 0.3))));
          const pts = computeGraphPoints(fn.expression, xMin, xMax, sampleCount);
          ctx.save();
          ctx.strokeStyle = fn.color;
          ctx.lineWidth = fnLineWidth;
          ctx.lineJoin = 'round';
          if (theme === 'dark') {
            ctx.shadowColor = fn.color;
            ctx.shadowBlur = Math.max(3, 6 + zoomLog);
          }
          ctx.beginPath();
          let penDown = false;
          pts.forEach(({ x: wx, y: wy }) => {
            if (isNaN(wy) || !isFinite(wy) || wy < yMin - 100 || wy > yMax + 100) {
              penDown = false;
              return;
            }
            const { cx, cy } = worldToCanvas(wx, wy, coordSys);
            if (!penDown) { ctx.moveTo(cx, cy); penDown = true; }
            else ctx.lineTo(cx, cy);
          });
          ctx.stroke();
          ctx.restore();
        });

      // ---- Trails ---- (1パスで描画してコンテキスト切り替えを最小化)
      if (showTrails) {
        objects.forEach((obj) => {
          const tLen = obj.trail.length;
          if (tLen < 2) return;
          // 軌跡が長い場合は間引いて描画
          const step = tLen > 80 ? 2 : 1;
          ctx.save();
          ctx.lineWidth = 1.5;
          ctx.lineJoin = 'round';
          // グラデーション代わりに数セグメントごとに色を変える（ctx切り替え回数を削減）
          const segments = Math.min(6, Math.ceil(tLen / step));
          const segSize = Math.ceil(tLen / segments);
          for (let seg = 0; seg < segments; seg++) {
            const alpha = (seg + 1) / segments;
            ctx.strokeStyle = obj.color + Math.round(alpha * 160).toString(16).padStart(2, '0');
            ctx.beginPath();
            const startIdx = seg * segSize;
            const endIdx = Math.min((seg + 1) * segSize, tLen - 1);
            if (startIdx >= tLen || !obj.trail[startIdx]) continue;
            const p0 = worldToCanvas(obj.trail[startIdx].x, obj.trail[startIdx].y, coordSys);
            ctx.moveTo(p0.cx, p0.cy);
            for (let i = startIdx + step; i <= endIdx; i += step) {
              if (!obj.trail[i]) continue;
              const p = worldToCanvas(obj.trail[i].x, obj.trail[i].y, coordSys);
              ctx.lineTo(p.cx, p.cy);
            }
            ctx.stroke();
          }
          ctx.restore();
        });
      }

      // ---- Objects ----
      // r is based on world units so the object scales with zoom
      // 重要: 物理判定はワールド座標で行われるため、描画もワールド座標と一致させる必要がある
      // pxPerUnitX と pxPerUnitY が異なる場合（非正方形画面）、円の描画に pxPerUnitX を使うと
      // Y方向のスケールとズレて隙間が生じる。物理判定のY座標は pxPerUnitY で変換されるため、
      // 描画の半径も Y方向スケール (pxPerUnitY) に合わせる必要がある。
      objects.forEach((obj) => {
        const { cx, cy } = worldToCanvas(obj.x, obj.y, coordSys);
        const isSelected = obj.id === selectedObjectId;
        // 物理判定と同じ半径計算を使用（三角形は TRIANGLE_SCALE 含む）
        const radiusWorld = objectRadiusWorld(obj);
        const r = radiusWorld * pxPerUnitY;

        // Skip drawing if object center is outside visible area
        if (
          obj.x < xMin - radiusWorld ||
          obj.x > xMax + radiusWorld ||
          obj.y < yMin - radiusWorld ||
          obj.y > yMax + radiusWorld
        ) return;

        ctx.save();
        ctx.translate(cx, cy);
        ctx.rotate(obj.angle);

        if (isSelected && theme === 'dark') {
          ctx.shadowColor = obj.color;
          ctx.shadowBlur = 20;
        }

        const strokeAlpha = isSelected ? (theme === 'picture' ? 'rgba(0,0,0,0.8)' : '#ffffff') : 'rgba(255,255,255,0.3)';
        ctx.strokeStyle = strokeAlpha;
        ctx.lineWidth = isSelected ? 2.5 : 1;

        if (obj.shape === 'circle') {
          const grad = ctx.createRadialGradient(-r * 0.3, -r * 0.3, r * 0.05, 0, 0, r);
          grad.addColorStop(0, 'rgba(255,255,255,0.5)');
          grad.addColorStop(0.35, obj.color);
          grad.addColorStop(1, shadeHex(obj.color, -40));
          ctx.fillStyle = grad;
          ctx.beginPath(); ctx.arc(0, 0, r, 0, Math.PI * 2); ctx.fill(); ctx.stroke();

          ctx.save();
          ctx.strokeStyle = 'rgba(255,255,255,0.55)';
          ctx.lineWidth = 1.5;
          ctx.setLineDash([r * 0.4, r * 0.6]);
          ctx.beginPath(); ctx.arc(0, 0, r * 0.65, 0, Math.PI * 2); ctx.stroke();
          ctx.setLineDash([]);
          ctx.lineWidth = 2;
          ctx.strokeStyle = 'rgba(255,255,255,0.7)';
          ctx.beginPath(); ctx.moveTo(0, 0); ctx.lineTo(r, 0); ctx.stroke();
          ctx.fillStyle = 'rgba(255,255,255,0.22)';
          ctx.beginPath(); ctx.arc(-r * 0.27, -r * 0.27, r * 0.32, 0, Math.PI * 2); ctx.fill();
          ctx.restore();
        } else if (obj.shape === 'square') {
          ctx.fillStyle = obj.color;
          ctx.beginPath(); ctx.rect(-r, -r, r * 2, r * 2); ctx.fill(); ctx.stroke();
          ctx.save();
          ctx.strokeStyle = 'rgba(255,255,255,0.25)';
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(-r, 0); ctx.lineTo(r, 0);
          ctx.moveTo(0, -r); ctx.lineTo(0, r);
          ctx.moveTo(-r, -r); ctx.lineTo(r, r);
          ctx.stroke();
          ctx.fillStyle = 'rgba(255,255,255,0.6)';
          ctx.beginPath(); ctx.arc(r * 0.6, -r * 0.6, r * 0.15, 0, Math.PI * 2); ctx.fill();
          ctx.restore();
        } else if (obj.shape === 'triangle') {
          // objectRadiusWorld が TRIANGLE_SCALE を含むため、r をそのまま使用
          ctx.fillStyle = obj.color;
          ctx.beginPath();
          ctx.moveTo(0, -r); ctx.lineTo(r * 0.866, r * 0.5); ctx.lineTo(-r * 0.866, r * 0.5);
          ctx.closePath(); ctx.fill(); ctx.stroke();
          ctx.save();
          ctx.strokeStyle = 'rgba(255,255,255,0.3)';
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(0, -r * 0.5); ctx.lineTo(r * 0.433, r * 0.25); ctx.lineTo(-r * 0.433, r * 0.25);
          ctx.closePath(); ctx.stroke();
          ctx.fillStyle = 'rgba(255,255,255,0.5)';
          ctx.beginPath(); ctx.arc(0, -r * 0.7, r * 0.12, 0, Math.PI * 2); ctx.fill();
          ctx.restore();
        }

        ctx.restore();

        // Velocity arrow for selected
        if (isSelected) {
          const vScale = 20;
          const vex = cx + obj.vx * vScale;
          const vey = cy - obj.vy * vScale;
          drawArrow(ctx, cx, cy, vex, vey, theme === 'picture' ? '#0070a0' : '#00d9ff', 2, 'v');
        }
      });
    },
    []
  );

  return { canvasRef, draw };
}

function shadeHex(hex: string, amount: number): string {
  const num = parseInt(hex.replace('#', ''), 16);
  const r = Math.max(0, Math.min(255, (num >> 16) + amount));
  const g = Math.max(0, Math.min(255, ((num >> 8) & 0xff) + amount));
  const b = Math.max(0, Math.min(255, (num & 0xff) + amount));
  return `rgb(${r},${g},${b})`;
}

function drawArrow(
  ctx: CanvasRenderingContext2D,
  x1: number, y1: number, x2: number, y2: number,
  color: string, lineWidth: number, label: string
) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len = Math.sqrt(dx * dx + dy * dy);
  if (len < 5) return;
  const angle = Math.atan2(dy, dx);

  ctx.save();
  ctx.strokeStyle = color;
  ctx.fillStyle = color;
  ctx.lineWidth = lineWidth;
  ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke();

  const headLen = 8;
  ctx.beginPath();
  ctx.moveTo(x2, y2);
  ctx.lineTo(x2 - headLen * Math.cos(angle - Math.PI / 6), y2 - headLen * Math.sin(angle - Math.PI / 6));
  ctx.lineTo(x2 - headLen * Math.cos(angle + Math.PI / 6), y2 - headLen * Math.sin(angle + Math.PI / 6));
  ctx.closePath();
  ctx.fill();

  ctx.font = '10px Inter, sans-serif';
  ctx.fillText(label, x2 + 6, y2 - 4);
  ctx.restore();
}
